/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import {
  Container, Typography, Box, Grid, Button, CircularProgress, Chip
} from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface FestivalSupportContent {
  title: string;
  subtitle: string;
  description: string;
  ulsavamImportance: string;
  impactTitle: string;
  impact1Title: string;
  impact1Description: string;
  impact2Title: string;
  impact2Description: string;
  impact3Title: string;
  impact3Description: string;
  howToContributeTitle: string;
  contribution1Title: string;
  contribution1Description: string;
  contribution2Title: string;
  contribution2Description: string;
  contribution3Title: string;
  contribution3Description: string;
}

const defaultContent: FestivalSupportContent = {
  title: 'Ulsavam Contributions',
  subtitle: 'Support the Grandeur of Our Annual Ulsavam Festival',
  description: 'Ulsavam is the most significant and prestigious festival of both Kottayil and Kovilakam temples. This is the main attraction that draws devotees from all over the region to witness the sacred celebrations, rituals, and grand processions.',
  ulsavamImportance: 'The annual Ulsavam (March 9-15) is the pinnacle of temple celebrations at both Kottayil and Kovilakam. Your contributions ensure we can maintain the highest standards of ritual excellence, cultural programs, decorations, and community service that make our Ulsavam the most cherished festival in the region.',
  impactTitle: 'Why Ulsavam Contributions Matter',
  impact1Title: 'Ritual Excellence',
  impact1Description: 'Sponsor ritual offerings and sacred ceremonies that are performed during Ulsavam. Your contribution helps maintain the authentic traditions and sacred practices.',
  impact2Title: 'Festival Grandeur',
  impact2Description: 'Support festival decorations, lighting arrangements, and procession materials. Ensure the gods are celebrated with the splendor and devotion they deserve.',
  impact3Title: 'Community Blessings',
  impact3Description: 'Enable complete festival celebrations including community feasts, prasadam distribution, and welfare activities for all attendees during the sacred days.',
  howToContributeTitle: 'Ways to Contribute During Ulsavam',
  contribution1Title: 'Ritual Offerings',
  contribution1Description: 'Your donations support the daily rituals, pujas, and sacred ceremonies performed at both Kottayil and Kovilakam temples during the Ulsavam period.',
  contribution2Title: 'Festival Decorations',
  contribution2Description: 'Help beautify our temples with traditional decorations, lighting, and ceremonial arrangements that make the Ulsavam experience memorable and sacred.',
  contribution3Title: 'Community Service',
  contribution3Description: 'Support community meals (Annadanam), prasadam preparation, and welfare services extended to all visiting devotees during the festival celebration period.'
};

export default function FestivalContribution() {
  const navigate = useNavigate();
  const [content, setContent] = useState<Partial<FestivalSupportContent>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const docRef = doc(db, 'festival_support_content', 'main');
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        try {
          if (docSnap.exists()) {
            const data = docSnap.data() as Partial<FestivalSupportContent>;
            setContent(data);
          } else {
            setContent({});
          }
        } catch (error) {
          console.error('Error processing festival content:', error);
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error('Festival Page Error:', error);
        setContent({});
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up listener:', error);
      setContent({});
      setLoading(false);
    }
  }, []);

  const safeContentEntries = Object.entries(content).filter(([, value]) => {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return value !== undefined && value !== null;
  });

  const displayContent: FestivalSupportContent = {
    ...defaultContent,
    ...(Object.fromEntries(safeContentEntries) as Partial<FestivalSupportContent>),
  };

  const ulsavamContributions = [
    {
      name: 'Kottayil and Kovilakam Temple Ulsavam',
      desc: 'Support the grand Ulsavam celebrations at both Kottayil and Kovilakam temples through rituals, processions, spiritual programs, and community gatherings during the sacred festival days.',
      Icon: TempleHinduIcon,
      gradient: 'linear-gradient(135deg, #b45309, #92400e)'
    },
  ];

  const impacts = [
    { title: displayContent.impact1Title, description: displayContent.impact1Description },
    { title: displayContent.impact2Title, description: displayContent.impact2Description },
    { title: displayContent.impact3Title, description: displayContent.impact3Description },
  ];

  const contributions = [
    { title: displayContent.contribution1Title, description: displayContent.contribution1Description },
    { title: displayContent.contribution2Title, description: displayContent.contribution2Description },
    { title: displayContent.contribution3Title, description: displayContent.contribution3Description },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress sx={{ color: '#a855f7' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Festival Contribution — Kottayil Temple | ഉത്സവ സംഭാവന" description="Support festivals at Kottayil Temple (Kotta-Kovilakam Kshethram). Contribute to annual ulsavam, pooram, and special celebrations at Muchukunnu." path="/donate/festival" keywords="Kottayil temple festival donation, ulsavam contribution, temple festival support, ഉത്സവ സംഭാവന, കൊട്ടയിൽ ക്ഷേത്രം ഉത്സവം, festival donation Kerala" />
      <PageBanner title={displayContent.title} />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ══════ VIBRANT HERO ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', mb: 3.5,
              background: 'linear-gradient(135deg, #581c87 0%, #9333ea 50%, #a855f7 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3.5, sm: 4 }, pb: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CelebrationIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip label="Annual Ulsavam March 9-15" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.7rem', mb: 1, height: 24, backdropFilter: 'blur(8px)' }} />
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', sm: '1.8rem' }, color: '#fff', lineHeight: 1.2 }}>
                      {displayContent.subtitle}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: 600 }}>
                  {displayContent.description}
                </Typography>
              </Box>
            </Box>

            {/* ══════ ULSAVAM IMPORTANCE ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 3.5,
              p: { xs: 2.5, sm: 3.5 }
            }}>
              <Typography sx={{ fontSize: '0.88rem', color: '#555', lineHeight: 1.8, fontWeight: 500 }}>
                {displayContent.ulsavamImportance}
              </Typography>
            </Box>

            {/* ══════ TEMPLE ULSAVAM CARDS ══════ */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
              {ulsavamContributions.map((temple, idx) => (
                <Grid item xs={12} key={idx}>
                  <Box sx={{
                    bgcolor: '#fff', borderRadius: '24px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 50px rgba(0,0,0,0.08)', '& .templeIcon': { transform: 'scale(1.15) rotate(8deg)' } },
                  }}>
                    <Box sx={{ background: temple.gradient, px: 2.5, pt: 2.5, pb: 4, position: 'relative', overflow: 'hidden' }}>
                      <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '12px 12px' }} />
                      <Box className="templeIcon" sx={{
                        width: 52, height: 52, borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                        border: '2px solid rgba(255,255,255,0.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        position: 'relative', zIndex: 2,
                        transition: 'transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }}>
                        <temple.Icon sx={{ fontSize: 26, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', mt: 1.5, position: 'relative', zIndex: 2 }}>
                        {temple.name}
                      </Typography>
                      <Box sx={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 16, bgcolor: '#fff', borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
                    </Box>
                    <Box sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#666', lineHeight: 1.7, mb: 2 }}>{temple.desc}</Typography>
                      <Button
                        fullWidth size="small"
                        onClick={() => navigate('/donate/online')}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '12px !important' }} />}
                        sx={{
                          background: 'linear-gradient(135deg, #b45309, #92400e)',
                          color: '#fff', fontWeight: 700, py: 0.9, borderRadius: '12px',
                          fontSize: '0.72rem', textTransform: 'none',
                          boxShadow: '0 4px 14px rgba(180,83,9,0.25)',
                          '&:hover': { boxShadow: '0 6px 20px rgba(180,83,9,0.35)' },
                        }}
                      >
                        Make a Contribution
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* ══════ IMPACT SECTION (Dark Slate) ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', mb: 3.5,
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', bottom: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(168,85,247,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3.5, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #e5c158)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>
                    {displayContent.impactTitle}
                  </Typography>
                </Box>

                {impacts.map((impact, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#a855f7', flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.78rem', color: '#fff', fontWeight: 700, mb: 0.3 }}>
                        {impact.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                        {impact.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ══════ CTA TO ONLINE DONATION ══════ */}
                        {/* ══════ HOW TO CONTRIBUTE SECTION ══════ */}
                        <Box sx={{
                          bgcolor: '#fff', borderRadius: '24px',
                          border: '1px solid rgba(0,0,0,0.06)',
                          boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                          overflow: 'hidden', mb: 3.5,
                        }}>
                          <Box sx={{
                            background: 'linear-gradient(135deg, #b45309, #92400e)',
                            px: { xs: 2.5, sm: 3 }, py: 2.5,
                            display: 'flex', alignItems: 'center', gap: 1.5,
                          }}>
                            <Box sx={{
                              width: 40, height: 40, borderRadius: '12px',
                              bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                              border: '2px solid rgba(255,255,255,0.25)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <CelebrationIcon sx={{ fontSize: 20, color: '#fff' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                              {displayContent.howToContributeTitle}
                            </Typography>
                          </Box>

                          <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 3 }}>
                            <Grid container spacing={2.5}>
                              {contributions.map((contrib, idx) => (
                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                  <Box sx={{
                                    bgcolor: '#f8fafc', borderRadius: '16px',
                                    border: '1px solid rgba(180,83,9,0.1)',
                                    p: 2.5, h: '100%',
                                    transition: 'all 300ms ease',
                                    '&:hover': { 
                                      transform: 'translateY(-6px)',
                                      boxShadow: '0 12px 32px rgba(180,83,9,0.1)',
                                      bgcolor: '#fff'
                                    }
                                  }}>
                                    <Box sx={{
                                      width: 44, height: 44, borderRadius: '12px',
                                      background: 'linear-gradient(135deg, #b45309, #92400e)',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      mb: 1.5
                                    }}>
                                      <CelebrationIcon sx={{ fontSize: 22, color: '#fff' }} />
                                    </Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#1e293b', mb: 1 }}>
                                      {contrib.title}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#666', lineHeight: 1.6 }}>
                                      {contrib.description}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Box>

            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #b45309 0%, #92400e 100%)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.06, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', bottom: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 3, sm: 4 }, position: 'relative', zIndex: 2, textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.4rem', sm: '1.8rem' }, color: '#fff', mb: 1.5, lineHeight: 1.2 }}>
                  Ready to Support Ulsavam?
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)', mb: 2.5, maxWidth: 500, mx: 'auto', lineHeight: 1.7 }}>
                  Your contribution will help us celebrate the grand Ulsavam festival at both Kottayil and Kovilakam temples with full splendor and devotion.
                </Typography>
                <Button
                  onClick={() => navigate('/donate/online')}
                  size="large"
                  sx={{
                    background: '#fff',
                    color: '#b45309',
                    fontWeight: 800, py: 1.2, px: 3.5, borderRadius: '16px',
                    fontSize: '0.9rem', textTransform: 'none',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.2)', transform: 'translateY(-2px)' },
                  }}
                >
                  Donate Now Online
                </Button>
              </Box>
            </Box>

          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="donate" activePath="/donate/festival" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
